
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EmailStatusIndicator } from "@/components/EmailStatusIndicator";
import { EmailTroubleshootingTips } from "@/components/EmailTroubleshootingTips";

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailStatus, setEmailStatus] = useState<'sent' | 'error' | 'confirmed'>('sent');
  const { toast } = useToast();
  const navigate = useNavigate();

  const email = searchParams.get('email') || '';
  const isTrialSignup = searchParams.get('trial') === 'true';
  const confirmed = searchParams.get('confirmed') === 'true';

  // Handle confirmed email redirect
  useEffect(() => {
    if (confirmed) {
      setEmailStatus('confirmed');
      // Auto-redirect to login after 3 seconds
      const timer = setTimeout(() => {
        navigate('/login?confirmed=true');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [confirmed, navigate]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Email address not found. Please try signing up again.",
        variant: "destructive"
      });
      return;
    }

    setResendLoading(true);
    setEmailStatus('sent'); // Reset status
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/email-verified`
        }
      });

      if (error) {
        setEmailStatus('error');
        toast({
          title: "Resend failed",
          description: error.message,
          variant: "destructive"
        });
      } else {
        setEmailStatus('sent');
        toast({
          title: "Email sent!",
          description: "We've sent you a new confirmation email.",
        });
        setResendCooldown(60); // 60 second cooldown
      }
    } catch (error) {
      setEmailStatus('error');
      toast({
        title: "Error",
        description: "Failed to resend email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setResendLoading(false);
    }
  };

  const getEmailProvider = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase();
    const providers = {
      'gmail.com': 'https://mail.google.com',
      'outlook.com': 'https://outlook.live.com',
      'hotmail.com': 'https://outlook.live.com',
      'yahoo.com': 'https://mail.yahoo.com',
      'icloud.com': 'https://www.icloud.com/mail'
    };
    return providers[domain as keyof typeof providers];
  };

  const emailProviderUrl = getEmailProvider(email);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="shadow-lg border border-border bg-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-foreground">RentFlow</span>
            </div>
            
            <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            
            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
              {confirmed ? "Email Confirmed!" : "Check your email"}
            </CardTitle>
            <CardDescription className="text-muted-foreground text-sm sm:text-base">
              {confirmed 
                ? "Your account is now verified. Redirecting to sign in..."
                : isTrialSignup 
                  ? "We've sent a confirmation link to activate your trial account"
                  : "We've sent a confirmation link to complete your registration"
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Email Status Indicator */}
            <EmailStatusIndicator status={emailStatus} email={email} />

            {/* Email address display */}
            {email && !confirmed && (
              <div className="bg-muted/30 border border-border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Email sent to:</p>
                <p className="font-medium text-foreground text-sm break-all">{email}</p>
              </div>
            )}

            {!confirmed && (
              <>
                {/* Instructions */}
                <div className="space-y-3 text-sm">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Check your inbox</p>
                      <p className="text-xs text-muted-foreground">Look for an email from RentFlow</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Click the confirmation link</p>
                      <p className="text-xs text-muted-foreground">This will verify your account</p>
                    </div>
                  </div>
                </div>

                {/* Email provider shortcut */}
                {emailProviderUrl && (
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a href={emailProviderUrl} target="_blank" rel="noopener noreferrer">
                      <Mail className="h-4 w-4 mr-2" />
                      Open {email.split('@')[1]}
                    </a>
                  </Button>
                )}

                {/* Troubleshooting Tips */}
                <EmailTroubleshootingTips />

                {/* Resend email */}
                <div className="pt-4 border-t border-border">
                  <div className="text-center space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Still not seeing the email?
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleResendEmail}
                      disabled={resendLoading || resendCooldown > 0}
                      className="w-full"
                    >
                      {resendLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : resendCooldown > 0 ? (
                        <>
                          Resend in {resendCooldown}s
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Resend confirmation email
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {confirmed && (
              <div className="text-center space-y-4">
                <Button 
                  asChild 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Link to="/login?confirmed=true">
                    Continue to Sign In
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground">
                  Redirecting automatically in a few seconds...
                </p>
              </div>
            )}

            {/* Sign in link for non-confirmed users */}
            {!confirmed && (
              <div className="text-center pt-2">
                <p className="text-sm text-muted-foreground">
                  Already confirmed your email?{" "}
                  <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                    Sign in to your account
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailConfirmation;
